/**
 * Mobile Watch List columns — 2-row (Season / Last-14) for MobileDynastyTable.
 *
 * The watch-list payload is MULTI-LEAGUE: player.league_contexts[] (one per
 * league). This factory is given the active leagueId and selects that league's
 * context (league_contexts.find(lc => lc.league_id === leagueId)) for ownership,
 * Price, and MV — the desktop pattern. The FA factory (singular league_context)
 * is deliberately NOT reused here.
 *
 * Price (baseline_price) + MV (current_market_value) come from the new per-context
 * financial enrichment (watchlist_service get_player_league_status), same source
 * as FA Search (player_prices). Honest-null → '-'.
 *
 * Colors from the shared service: POS/Price/YOURS = warning/primary tokens,
 * MV/FA = success (#10b981). team-abbr blue #60a5fa = C.blue token,
 * matching the locked ownership palette + OwnerBadge.
 */
import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import MobilePlayerLink from './MobilePlayerLink';
import MobileInjuryBadge from './MobileInjuryBadge';

const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;
const AMBER = dynastyTheme.tokens.colors.warning;   // #f59e0b — Price + rolling values
const GREEN = dynastyTheme.tokens.colors.success;   // #10b981 — MV + FA chip
const TEAM_BLUE = C.blue;                             // #60a5fa — locked ownership blue
const WHITE = dynastyTheme.tokens.colors.neutral[50];
const MUTED = dynastyTheme.tokens.colors.neutral[500];

// Active league context for ownership / Price / MV (desktop .find pattern).
export const getActiveContext = (player, leagueId) =>
  (player?.league_contexts || []).find((lc) => lc.league_id === leagueId) || null;

const fmt = (v, decimals) => {
  if (v == null) return '-';
  if (typeof v === 'number') return decimals != null ? v.toFixed(decimals).replace(/^0\./, '.') : v;
  return v;
};

// Read a stat for the current row's window (season row vs rolling row).
const statFor = (player, rowType, side, key) => {
  const slot = rowType === 'rolling' ? 'rolling_14_day' : 'season';
  return player?.stats?.[slot]?.[side]?.[key];
};

// Build the stat columns for a side (hitting/pitching). Season row = white,
// rolling row = amber italic.
const statCol = (side, key, header, { width = '28px', decimals = null } = {}) => ({
  key: `stat_${key}`,
  header,
  width,
  textAlign: 'center',
  accessor: (row) => statFor(row.player, row.statType, side, key),
  render: (value, row) => {
    const isRolling = row.statType === 'rolling';
    return (
      <span style={{ color: isRolling ? AMBER : WHITE, fontStyle: isRolling ? 'italic' : 'normal', fontVariantNumeric: 'tabular-nums' }}>
        {fmt(value, decimals)}
      </span>
    );
  },
});

const HIT_STATS = [
  ['games_played', 'G'], ['at_bats', 'AB'], ['runs', 'R'], ['hits', 'H'],
  ['home_runs', 'HR'], ['rbi', 'RBI'], ['stolen_bases', 'SB'], ['walks', 'BB'],
  ['strikeouts', 'SO'], ['batting_avg', 'AVG', { decimals: 3, width: '40px' }],
  ['ops', 'OPS', { decimals: 3, width: '42px' }],
];
const PIT_STATS = [
  ['games_played', 'G'], ['games_started', 'GS'], ['innings_pitched', 'IP', { decimals: 1, width: '40px' }],
  ['wins', 'W'], ['saves', 'S'], ['era', 'ERA', { decimals: 2, width: '42px' }],
  ['whip', 'WHIP', { decimals: 2, width: '44px' }], ['strikeouts_pitched', 'K'],
  ['quality_starts', 'QS'],
];

// Ownership chip from the active league context. YOURS gold / team-abbr blue / FA green.
const ownChip = (ctx) => {
  if (!ctx || ctx.status === 'not_in_league') return null;
  const base = { fontSize: 8, fontWeight: 700, padding: '1px 4px', borderRadius: 2, whiteSpace: 'nowrap' };
  if (ctx.status === 'owned' && ctx.team?.is_user_team) {
    return <span style={{ ...base, backgroundColor: C.primaryTint.t13, color: GOLD }}>YOURS</span>;
  }
  if (ctx.status === 'owned' || ctx.status === 'other_team') {
    return <span style={{ ...base, backgroundColor: C.ownBadge.teamBg, color: TEAM_BLUE }}>{(ctx.team?.team_name || 'TEAM').substring(0, 10)}</span>;
  }
  return <span style={{ ...base, backgroundColor: C.ownBadge.faBg, color: GREEN }}>FA</span>;
};

const buildColumns = (isPitcher, { leagueId, onRemove, onAdd, onPlayerClick }) => {
  const side = isPitcher ? 'pitching' : 'hitting';
  const stats = isPitcher ? PIT_STATS : HIT_STATS;

  return [
    // POS (frozen) — gold, season row only
    {
      key: 'position_slot', header: 'POS', width: '34px', minWidth: '34px', maxWidth: '34px',
      textAlign: 'center', sticky: true, fontSize: '9px',
      accessor: (row) => row.player?.info?.position || '-',
      render: (value, row) => (row.statType === 'season'
        ? <span style={{ color: AMBER, fontWeight: 700 }}>{value}</span> : null),
    },
    // Player (frozen) — name on season row, "14-Day" label on rolling row
    {
      key: 'player_name', header: 'Player', width: '108px', minWidth: '108px', maxWidth: '108px',
      textAlign: 'left', sticky: true, nowrap: true, ellipsis: true,
      accessor: (row) => row.player?.info?.full_name
        || [row.player?.info?.first_name, row.player?.info?.last_name].filter(Boolean).join(' '),
      render: (value, row) => {
        if (row.statType === 'rolling') {
          return <span style={{ fontSize: 10, fontStyle: 'italic', color: AMBER }}>14-Day</span>;
        }
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
            <MobilePlayerLink
              mlbId={row.player?.ids?.mlb}
              name={value}
              onClick={onPlayerClick}
              style={{ fontWeight: 500, fontSize: 'inherit', color: WHITE }}
            />
            <MobileInjuryBadge mlbId={row.player?.ids?.mlb} />
          </span>
        );
      },
    },
    // Ownership chip from the active league context (season row)
    {
      key: 'ownership', header: 'Own', width: '54px', textAlign: 'center',
      accessor: (row) => getActiveContext(row.player, leagueId)?.status || '',
      render: (value, row) => (row.statType === 'season' ? ownChip(getActiveContext(row.player, leagueId)) : null),
    },
    // MLB team (season row)
    {
      key: 'mlb_team', header: 'Tm', width: '34px', textAlign: 'center', fontSize: '9px',
      accessor: (row) => row.player?.info?.mlb_team || '-',
      render: (value, row) => (row.statType === 'season'
        ? <span style={{ color: MUTED }}>{value}</span> : null),
    },
    // Stat columns (season white / rolling amber-italic)
    ...stats.map(([key, header, opts]) => statCol(side, key, header, opts)),
    // Price — gold, from the new league-scoped baseline_price (season row)
    {
      key: 'price', header: 'Price', width: '46px', textAlign: 'center', fontSize: '10px',
      accessor: (row) => getActiveContext(row.player, leagueId)?.financial?.baseline_price,
      render: (value, row) => {
        if (row.statType !== 'season') return null;
        return <span style={{ color: AMBER, fontWeight: 600 }}>{value != null ? `$${Math.round(value)}` : '-'}</span>;
      },
    },
    // MV — green, from the new current_market_value (season row)
    {
      key: 'market_value', header: 'MV', width: '46px', textAlign: 'center', fontSize: '10px',
      accessor: (row) => getActiveContext(row.player, leagueId)?.financial?.current_market_value,
      render: (value, row) => {
        if (row.statType !== 'season') return null;
        return <span style={{ color: GREEN, fontWeight: 600 }}>{value != null ? `$${Math.round(value)}` : '-'}</span>;
      },
    },
    // Actions — remove (always) + add-to-roster (FA in the active league), season row
    {
      key: 'actions', header: '', width: '60px', textAlign: 'center',
      accessor: (row) => row,
      render: (value, row) => {
        if (row.statType !== 'season') return null;
        const player = row.player;
        const ctx = getActiveContext(player, leagueId);
        const isAvailable = ctx?.status === 'available';
        return (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
            {isAvailable && onAdd && (
              <button
                onClick={(e) => { e.stopPropagation(); onAdd(player); }}
                title="Add to roster"
                style={{ fontSize: 8, fontWeight: 700, padding: '2px 5px', borderRadius: 2, border: 'none', cursor: 'pointer', backgroundColor: C.primaryTint.t13, color: GOLD }}
              >ADD</button>
            )}
            {onRemove && (
              <button
                onClick={(e) => { e.stopPropagation(); onRemove(player?.ids?.mlb); }}
                title="Remove from watch list"
                aria-label="Remove from watch list"
                style={{ fontSize: 11, lineHeight: 1, padding: '2px 5px', borderRadius: 2, border: 'none', cursor: 'pointer', backgroundColor: 'transparent', color: dynastyTheme.tokens.colors.error }}
              >✕</button>
            )}
          </div>
        );
      },
    },
  ];
};

export const createMobileWatchHitterColumns = (cfg) => buildColumns(false, cfg);
export const createMobileWatchPitcherColumns = (cfg) => buildColumns(true, cfg);
